import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { FindUsersFilters, FindUsersParams, FindUsersResponse, UserListItem } from '../types/user.type';
import { ADMIN_USERS_API_PATHS } from '../constants/users-paths.constants';

export type GetUsersRequest = Omit<FindUsersParams, 'filters'> & { filters: FindUsersFilters };

type UseAdminGetUsersResult = {
  isLoading: boolean;
  isRefetching: boolean;
  totalCount: number;
  users: UserListItem[];
  refetch: () => Promise<void>;
};

const INITIAL_STATE: Omit<UseAdminGetUsersResult, 'refetch'> = {
  isLoading: true,
  isRefetching: false,
  totalCount: 0,
  users: [],
};

export function useAdminGetUsers({
  page,
  limit,
  sortBy,
  sortDirection,
  filters,
}: GetUsersRequest): UseAdminGetUsersResult {
  const { enqueueSnackbar } = useSnackbar();
  const [result, setResult] = useState(INITIAL_STATE);

  const serializedFilters = JSON.stringify(filters);

  const refetch = useCallback(async (): Promise<void> => {
    setResult((previous) => (previous.isLoading ? previous : { ...previous, isRefetching: true }));

    try {
      const response = await HttpClient.get<FindUsersResponse>(ADMIN_USERS_API_PATHS.GET_USERS, {
        params: { page, limit, sortBy, sortDirection, filters: serializedFilters },
      });

      setResult({
        isLoading: false,
        isRefetching: false,
        totalCount: response.totalCount,
        users: response.users,
      });
    } catch (error) {
      enqueueSnackbar(
        extractTranslatedApiErrorMessage(error, 'Une erreur est survenue lors de la récupération des utilisateurs'),
        { variant: 'error' }
      );
      setResult({ isLoading: false, isRefetching: false, totalCount: 0, users: [] });
    }
  }, [page, limit, sortBy, sortDirection, serializedFilters, enqueueSnackbar]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { ...result, refetch };
}
